import React, { useEffect, useState, useRef, useMemo } from 'react';
import { AreaChart, Area, ResponsiveContainer, YAxis, XAxis, ReferenceLine, ReferenceDot, ReferenceArea, Line } from 'recharts';
import { PositionBoundaries } from '../../utils/positionBoundaries';

interface MiniStockChartProps {
  symbol: string;
  height?: number;
  showVolume?: boolean;
  className?: string;
  strikePrice?: number;
  entryPrice?: number;
  entryPremium?: number; // Entry premium for safety floor calculation
  entryDate?: string;
  expiryDate?: string;
  periodMonths?: number;
  timespan?: 'day' | 'week';
  currentDelta?: number;
  positionType?: string; // 'covered_call' | 'cash_secured_put' (BAB-40) - flips the zone/line logic
  chartData?: { results: any[] }; // Optional pre-loaded chart data for performance
  realtimePrice?: number; // Current price from aggregate table (15-min delayed)
  showLegend?: boolean; // Control legend visibility (default: true)
  onOpenFullscreen?: () => void; // Callback to open fullscreen chart modal
  legHistory?: any[]; // Campaign model - previous leg entries (deprecated, pass empty array)
  /** BAB-67: per-position decision-map overlays. Optional — chart renders
   *  unchanged when absent. The 4 boundary primitives drive 2 ReferenceLines
   *  (stock_stop horizontal + time_stop vertical) and 2 Line curves
   *  (profit_target + delta_breach). All curves render in the FUTURE part of
   *  the chart only; historical area remains untouched. */
  boundaries?: PositionBoundaries | null;
  /** 'split' (default): green/red split at entry/strike, for positions.
   *  'plain': one neutral line, dashed start level, period change label, for the screener card. */
  variant?: 'split' | 'plain';
}

interface ChartDataPoint {
  price: number;
  relativePrice: number;
  timestamp: number;
  month: string;
  markerType?: 'entry' | 'expiry' | 'legEntry' | null;
  isMarker?: boolean;
  legNumber?: number; // For leg entry markers
}

const MiniStockChart: React.FC<MiniStockChartProps> = ({
  symbol,
  height = 160,
  showVolume = false,
  className = '',
  strikePrice,
  entryPrice,
  entryPremium,
  entryDate,
  expiryDate,
  periodMonths = 6,
  timespan = 'week',
  currentDelta,
  positionType = 'covered_call',
  chartData: preloadedChartData,
  realtimePrice,
  showLegend = true,
  onOpenFullscreen,
  legHistory,
  boundaries,
  variant = 'split'
}) => {
  const isCsp = positionType === 'cash_secured_put';
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isPositive, setIsPositive] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [waitingForBatch, setWaitingForBatch] = useState(true);

  const chartRef = useRef<HTMLDivElement>(null);
  const batchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasLoaded) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the component is visible
        rootMargin: '50px' // Start loading 50px before the component enters viewport
      }
    );

    if (chartRef.current) {
      observer.observe(chartRef.current);
    }

    return () => {
      if (chartRef.current) {
        observer.unobserve(chartRef.current);
      }
    };
  }, [hasLoaded]);

  useEffect(() => {
    const loadChartData = async () => {
      if (!symbol || !isVisible || hasLoaded) return;

      // Batch loading only - no individual API calls
      // If we have preloaded chart data from batch, use it
      if (preloadedChartData && preloadedChartData.results && preloadedChartData.results.length > 0) {
        // Clear any pending timeout
        if (batchTimeoutRef.current) {
          clearTimeout(batchTimeoutRef.current);
        }

        const startTime = performance.now();
        try {
          const startingPrice = preloadedChartData.results[0].close;
          // Use entryPrice as reference when available (green above buy price, red below)
          const referencePrice = entryPrice || startingPrice;
          const transformedData: ChartDataPoint[] = preloadedChartData.results.map((data) => {
            return {
              price: data.close,
              relativePrice: data.close - referencePrice,
              timestamp: data.timestamp,
              month: '',
              markerType: null,
              isMarker: false
            };
          });

          // Add previous leg entry points (Campaign Model) - subtle markers
          if (legHistory && legHistory.length > 0) {
            legHistory.forEach(leg => {
              const legTimestamp = new Date(leg.entryDate).getTime();
              const legEntryPoint: ChartDataPoint = {
                price: leg.stockPriceAtEntry,
                relativePrice: leg.stockPriceAtEntry - referencePrice,
                timestamp: legTimestamp,
                month: '',
                markerType: 'legEntry',
                isMarker: true,
                legNumber: leg.legNumber
              };

              let insertIndex = transformedData.findIndex(point => point.timestamp > legTimestamp);
              if (insertIndex === -1) {
                transformedData.push(legEntryPoint);
              } else {
                transformedData.splice(insertIndex, 0, legEntryPoint);
              }
            });
          }

          // Add current leg entry point if provided (current active position)
          if (entryDate && entryPrice) {
            const entryTimestamp = new Date(entryDate).getTime();
            const entryPoint: ChartDataPoint = {
              price: entryPrice,
              relativePrice: entryPrice - referencePrice,
              timestamp: entryTimestamp,
              month: '',
              markerType: 'entry',
              isMarker: true
            };

            let insertIndex = transformedData.findIndex(point => point.timestamp > entryTimestamp);
            if (insertIndex === -1) {
              transformedData.push(entryPoint);
            } else {
              transformedData.splice(insertIndex, 0, entryPoint);
            }
          }

          // Add realtime price as final point if provided and newer than last historical bar
          if (realtimePrice && realtimePrice > 0 && transformedData.length > 0) {
            const lastHistoricalTimestamp = transformedData[transformedData.length - 1].timestamp;
            const currentTimestamp = Date.now();

            // Only append if current timestamp is newer than last historical bar
            if (currentTimestamp > lastHistoricalTimestamp) {
              const realtimePricePoint: ChartDataPoint = {
                price: realtimePrice,
                relativePrice: realtimePrice - referencePrice,
                timestamp: currentTimestamp,
                month: '',
                markerType: null,
                isMarker: false
              };
              transformedData.push(realtimePricePoint);
              console.log(`📍 [Realtime Price] Appended real-time point for ${symbol}: $${realtimePrice.toFixed(2)}`);
            }
          }

          setChartData(transformedData);
          // Use realtime price if available, otherwise use last historical bar
          const lastPrice = (realtimePrice && realtimePrice > 0) ? realtimePrice : preloadedChartData.results[preloadedChartData.results.length - 1].close;
          setIsPositive(lastPrice >= referencePrice);
          setWaitingForBatch(false);
          setHasLoaded(true);
          const elapsed = performance.now() - startTime;
          console.log(`✅ [Chart ${symbol}] Processed ${preloadedChartData.results.length} bars in ${elapsed.toFixed(0)}ms`);
          return;
        } catch (err) {
          console.error(`❌ [Batch] Error processing cached data for ${symbol}:`, err);
          setError('Invalid cache data');
          setWaitingForBatch(false);
          setHasLoaded(true);
          return;
        }
      }

      // No batch data available yet - wait with grace period before showing error
      if (waitingForBatch && !batchTimeoutRef.current) {
        console.log(`⏳ [Batch] Waiting for batch data for ${symbol}...`);

        // Set a timeout to show error if batch doesn't arrive within 15 seconds
        batchTimeoutRef.current = setTimeout(() => {
          if (!hasLoaded) {
            console.log(`⚠️ [Batch] Timeout waiting for batch data for ${symbol}`);
            setError('No chart data');
            setWaitingForBatch(false);
            setHasLoaded(true);
          }
        }, 15000); // 15 second grace period
      }
    };

    loadChartData();

    // Cleanup timeout on unmount
    return () => {
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
    };
  }, [symbol, isVisible, hasLoaded, preloadedChartData, waitingForBatch]);

  // BAB-67: per-position decision-map overlays.
  // Translate the boundaries (in absolute prices) to relative-price space
  // (the chart's Y axis = price - refPrice). Curves become arrays of
  // {timestamp, value} ready for a Line component with its own data prop.
  const boundaryOverlays = useMemo(() => {
    if (!boundaries) return null;
    if (!entryPrice || !chartData.length) return null;
    const refPrice = entryPrice;

    const stockStopRel = boundaries.stockStopPrice !== null
      ? boundaries.stockStopPrice - refPrice
      : null;

    const profitCurveRel = boundaries.profitTargetCurve?.map((pt) => ({
      timestamp: pt.timestamp,
      value: pt.stockPrice - refPrice,
    })) ?? null;

    const deltaCurveRel = boundaries.deltaBreachCurve?.map((pt) => ({
      timestamp: pt.timestamp,
      value: pt.stockPrice - refPrice,
    })) ?? null;

    return {
      stockStopRel,
      timeStopTimestamp: boundaries.timeStopTimestamp,
      profitCurveRel,
      deltaCurveRel,
      ivUnavailable: boundaries.ivUnavailable,
    };
  }, [boundaries, entryPrice, chartData.length]);

  // Calculate profit zone boundaries (floor and ceiling lines)
  const profitZoneBounds = useMemo(() => {
    if (!entryPrice || !entryPremium || !strikePrice || chartData.length === 0) {
      return null;
    }

    // Reference price matches the chart's relativePrice base
    const refPrice = entryPrice;

    if (isCsp) {
      // CSP economics (sold put): keep the FULL premium while price stays
      // ABOVE the strike (put expires worthless). Between (strike - premium)
      // and the strike you'd be assigned but the premium still nets positive.
      // Below (strike - premium) the assigned shares lose more than the premium.
      const assignmentLine = strikePrice;                   // green-zone floor / assignment trigger
      const breakevenFloor = strikePrice - entryPremium;    // net break-even on assignment
      return {
        isCsp: true,
        relativeBreakeven: breakevenFloor - refPrice,
        relativeAssignment: (assignmentLine - refPrice) as number | null,
        relativeCeiling: assignmentLine - refPrice,          // alias so Y-domain includes the strike
        relativeProfileStop: null,
        breakevenFloor,
        ceiling: assignmentLine,
        profileStop: null as number | null,
      };
    }

    // Covered call (default): profit between break-even and the strike cap.
    const breakevenFloor = entryPrice - entryPremium;       // Premium recovery line
    const ceiling = strikePrice;                              // Max profit ceiling

    return {
      isCsp: false,
      relativeBreakeven: breakevenFloor - refPrice,
      relativeAssignment: null as number | null,
      relativeCeiling: ceiling - refPrice,
      relativeProfileStop: null,
      breakevenFloor,
      ceiling,
      profileStop: null as number | null,
    };
  }, [entryPrice, entryPremium, strikePrice, chartData, isCsp]);

  // Show loading state when component is visible but data is loading
  if (isVisible && isLoading) {
    return (
      <div
        ref={chartRef}
        className={`mini-stock-chart mini-stock-chart-loading ${className}`}
        style={{ height }}
      >
        <div className="mini-stock-chart-spinner"></div>
      </div>
    );
  }

  // Show skeleton state when waiting for batch data or not yet visible
  if (!isVisible || !hasLoaded || waitingForBatch) {
    return (
      <div
        ref={chartRef}
        className={`mini-stock-chart mini-stock-chart-skeleton ${className}`}
        style={{ height }}
      >
        <div className="mini-stock-chart-skeleton-content">
          <div className="mini-stock-chart-skeleton-line"></div>
        </div>
      </div>
    );
  }

  // Show error state only after batch timeout or actual error
  if (error || chartData.length === 0) {
    return (
      <div
        ref={chartRef}
        className={`mini-stock-chart mini-stock-chart-error ${className}`}
        style={{ height }}
      >
        <span className="mini-stock-chart-error-text">No chart data</span>
      </div>
    );
  }

  // Calculate gradient offset for split coloring.
  // CC splits green/red at the entry price (relativePrice = 0). For a CSP the
  // meaningful divide is the strike: above it the put is safe (green), below it
  // assignment risk grows (red), so split there instead.
  const calculateGradientOffset = () => {
    if (chartData.length === 0) return 0.5;

    const relativePrices = chartData.map(d => d.relativePrice);
    const dataMax = Math.max(...relativePrices);
    const dataMin = Math.min(...relativePrices);

    const splitPoint = (isCsp && entryPrice && strikePrice)
      ? strikePrice - entryPrice
      : 0;

    if (dataMax <= splitPoint) {
      return 0;
    }
    if (dataMin >= splitPoint) {
      return 1;
    }

    return (dataMax - splitPoint) / (dataMax - dataMin);
  };

  // Define gradient colors and properties
  const gradientId = `gradient-${symbol}`;
  const strokeGradientId = `stroke-gradient-${symbol}`;
  const gradientOffset = calculateGradientOffset();

  // Calculate current price for display
  const currentPrice = chartData.length > 0 ? chartData[chartData.length - 1].price : null;
  const currentRelativePrice = chartData.length > 0 ? chartData[chartData.length - 1].relativePrice : null;

  // Calculate domain that includes historical data plus space for expiry marker
  const calculateChartDomain = () => {
    if (chartData.length === 0) return ['dataMin', 'dataMax'];

    const timestamps = chartData.map(d => d.timestamp);
    const minTimestamp = Math.min(...timestamps);
    const maxTimestamp = Math.max(...timestamps);

    let domainMax = maxTimestamp;

    // If there's an expiry marker, ensure it's included in the domain
    if (expiryDate) {
      const expiryTimestamp = new Date(expiryDate).getTime();
      const currentTimestamp = Date.now();

      if (expiryTimestamp > currentTimestamp) {
        // Ensure expiry marker is within domain with adequate padding
        const timeRange = maxTimestamp - minTimestamp;
        const minPadding = timeRange * 0.15; // Minimum 15% padding
        const expiryBuffer = Math.max(minPadding, 7 * 24 * 60 * 60 * 1000); // At least 7 days buffer

        domainMax = Math.max(maxTimestamp, expiryTimestamp + expiryBuffer);
      }
    }

    // Add some padding to the left for better visualization
    const timeRange = domainMax - minTimestamp;
    const leftPadding = timeRange * 0.05;

    return [minTimestamp - leftPadding, domainMax];
  };

  const chartDomain = calculateChartDomain();

  // Calculate Y-axis domain to ensure floor, ceiling, and markers are visible
  const calculateYDomain = () => {
    if (chartData.length === 0) return ['dataMin', 'dataMax'];

    const relativePrices = chartData.map(d => d.relativePrice);
    let minPrice = Math.min(...relativePrices);
    let maxPrice = Math.max(...relativePrices);

    // Include floor, profile stop, and ceiling in Y domain if available
    if (profitZoneBounds) {
      minPrice = Math.min(minPrice, profitZoneBounds.relativeBreakeven);
      if (profitZoneBounds.relativeProfileStop !== null) {
        minPrice = Math.min(minPrice, profitZoneBounds.relativeProfileStop);
      }
      maxPrice = Math.max(maxPrice, profitZoneBounds.relativeCeiling);
    }

    // Add 10% padding to ensure markers are fully visible
    const priceRange = maxPrice - minPrice;
    const padding = Math.max(priceRange * 0.1, 1); // Minimum $1 padding

    return [minPrice - padding, maxPrice + padding];
  };

  const yDomain = calculateYDomain();

  // Calculate expiry marker position for ReferenceDot
  const calculateExpiryMarkerData = () => {
    if (!expiryDate || !strikePrice || chartData.length === 0) return null;

    try {
      const expiryTimestamp = new Date(expiryDate).getTime();
      const currentTimestamp = Date.now();
      const lastDataTimestamp = chartData[chartData.length - 1]?.timestamp || currentTimestamp;

      // Only show expiry marker if it's in the future
      if (expiryTimestamp <= currentTimestamp) return null;

      const refPrice = entryPrice || chartData[0].price;
      const relativeStrikePrice = strikePrice - refPrice;

      return {
        x: expiryTimestamp,
        y: relativeStrikePrice
      };
    } catch (error) {
      console.error('Error calculating expiry marker:', error);
      return null;
    }
  };

  const expiryMarkerData = calculateExpiryMarkerData();

  // Calculate headroom metrics for display
  const calculateHeadroomMetrics = () => {
    if (!strikePrice || !currentPrice || currentDelta === undefined) return null;

    // CC: room ABOVE current price up to the strike (cap). CSP: cushion the
    // price sits ABOVE the strike before assignment risk. Put delta is negative,
    // so use magnitude for assignment probability.
    const headroomAmount = isCsp ? (currentPrice - strikePrice) : (strikePrice - currentPrice);
    const headroomPercent = (headroomAmount / currentPrice) * 100;
    const probability = Math.abs(currentDelta) * 100;

    return {
      headroomAmount,
      headroomPercent,
      probability
    };
  };

  const headroomMetrics = calculateHeadroomMetrics();
  const shouldShowHeadroom = headroomMetrics !== null;

  // Handle chart container click - opens fullscreen on tap
  const handleChartClick = (e: React.MouseEvent) => {
    if (onOpenFullscreen) {
      e.stopPropagation();
      onOpenFullscreen();
    }
  };

  return (
    <div
      ref={chartRef}
      className={`mini-stock-chart ${className}`}
      style={{ height, position: 'relative', cursor: onOpenFullscreen ? 'pointer' : 'default' }}
      onClick={handleChartClick}
    >
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            filter: drop-shadow(0 2px 4px rgba(220, 38, 38, 0.3));
          }
          50% {
            opacity: 0.7;
            filter: drop-shadow(0 0 8px rgba(220, 38, 38, 0.8)) drop-shadow(0 0 12px rgba(220, 38, 38, 0.6));
          }
        }
      `}</style>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: variant === 'plain' ? 18 : 24, right: 4, left: 4, bottom: 4 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10B981" stopOpacity={0.8} />
              <stop offset={`${gradientOffset * 100}%`} stopColor="#10B981" stopOpacity={0} />
              <stop offset={`${gradientOffset * 100}%`} stopColor="#EF4444" stopOpacity={0} />
              <stop offset="100%" stopColor="#EF4444" stopOpacity={0.8} />
            </linearGradient>
            <linearGradient id={strokeGradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset={gradientOffset} stopColor="#10B981" stopOpacity={1} />
              <stop offset={gradientOffset} stopColor="#EF4444" stopOpacity={1} />
            </linearGradient>
          </defs>


          <XAxis
            hide
            dataKey="timestamp"
            type="number"
            scale="time"
            domain={chartDomain}
          />
          <YAxis hide domain={yDomain} />

          <Area
            type="monotone"
            dataKey="relativePrice"
            stroke={`url(#${strokeGradientId})`}
            strokeWidth={variant === 'plain' ? 1.5 : 2}
            fill={`url(#${gradientId})`}
            isAnimationActive={variant !== 'plain'}
            dot={(props: any) => {
              const { payload } = props;
              if (!payload || !payload.isMarker) {
                return <g />; // Return empty group for non-marker points
              }

              // Current leg entry - bright yellow/orange
              if (payload.markerType === 'entry') {
                return (
                  <circle
                    cx={props.cx}
                    cy={props.cy}
                    r={4}
                    fill="#F59E0B"
                    stroke="#fff"
                    strokeWidth={2}
                    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                  />
                );
              }

              // Previous leg entries - subtle muted color
              if (payload.markerType === 'legEntry') {
                return (
                  <circle
                    cx={props.cx}
                    cy={props.cy}
                    r={3}
                    fill="#6B7280"
                    stroke="#9CA3AF"
                    strokeWidth={1.5}
                    style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))', opacity: 0.7 }}
                  />
                );
              }

              return <g />;
            }}
            activeDot={false}
            animationDuration={1000}
            animationBegin={0}
          />

          {/* Keep-premium / Profit Zone (green).
              CC: between break-even and the strike cap.
              CSP: everything ABOVE the strike (put expires worthless). */}
          {profitZoneBounds && (
            <ReferenceArea
              y1={profitZoneBounds.isCsp ? (profitZoneBounds.relativeAssignment as number) : profitZoneBounds.relativeBreakeven}
              y2={profitZoneBounds.isCsp ? (yDomain[1] as number) : profitZoneBounds.relativeCeiling}
              fill="#10B981"
              fillOpacity={0.1}
            />
          )}

          {/* Watch / assigned-but-offset Zone (amber).
              CC: below break-even down to the profile stop (or chart bottom).
              CSP: between (strike - premium) and the strike — assigned, but the
              premium still covers the loss. */}
          {profitZoneBounds && (
            <ReferenceArea
              y1={
                profitZoneBounds.isCsp
                  ? profitZoneBounds.relativeBreakeven
                  : (profitZoneBounds.relativeProfileStop !== null
                      ? profitZoneBounds.relativeProfileStop
                      : (yDomain[0] as number))
              }
              y2={
                profitZoneBounds.isCsp
                  ? (profitZoneBounds.relativeAssignment as number)
                  : profitZoneBounds.relativeBreakeven
              }
              fill="#F59E0B"
              fillOpacity={0.12}
            />
          )}

          {/* Loss Zone (red) - CSP only: below the net break-even the assigned
              shares lose more than the premium collected. */}
          {profitZoneBounds && profitZoneBounds.isCsp && (
            <ReferenceArea
              y1={yDomain[0] as number}
              y2={profitZoneBounds.relativeBreakeven}
              fill="#EF4444"
              fillOpacity={0.1}
            />
          )}

          {/* Profile stop line - v6.0 Hull Breach level (only when available) */}
          {profitZoneBounds && profitZoneBounds.relativeProfileStop !== null && (
            <ReferenceLine
              y={profitZoneBounds.relativeProfileStop}
              stroke="#F59E0B"
              strokeWidth={1}
              strokeDasharray="3 3"
              strokeOpacity={0.7}
            />
          )}

          {/* Strike line - CC: max-profit cap (green). CSP: assignment trigger (amber). */}
          {profitZoneBounds && (
            <ReferenceLine
              y={profitZoneBounds.relativeCeiling}
              stroke={profitZoneBounds.isCsp ? '#F59E0B' : '#10B981'}
              strokeWidth={1}
              strokeOpacity={0.6}
            />
          )}

          {/* Break-even line - CC: buy price - premium (green). CSP: strike - premium,
              boundary into the loss zone (red). */}
          {profitZoneBounds && (
            <ReferenceLine
              y={profitZoneBounds.relativeBreakeven}
              stroke={profitZoneBounds.isCsp ? '#EF4444' : '#10B981'}
              strokeWidth={1}
              strokeOpacity={profitZoneBounds.isCsp ? 0.6 : 0.5}
            />
          )}

          {/* Expiry marker */}
          {expiryMarkerData && (
            <ReferenceDot
              x={expiryMarkerData.x}
              y={expiryMarkerData.y}
              r={5}
              fill="#DC2626"
              stroke="#fff"
              strokeWidth={2}
              style={{
                animation: 'pulse 2s ease-in-out infinite'
              }}
            />
          )}

          {/* BAB-67: Decision-map overlays.
              These re-render purely on intent / threshold changes — chart
              data fetch is unaffected (separate useEffect, deps on symbol
              + visibility only). Confirms the founder's "no price-chart
              refetch on intent flip" requirement. */}
          {boundaryOverlays?.stockStopRel != null && (
            <ReferenceLine
              y={boundaryOverlays.stockStopRel}
              stroke="#EF4444"
              strokeWidth={1.5}
              strokeDasharray="5 4"
              strokeOpacity={0.85}
              label={{
                value: 'Stock stop',
                position: 'insideLeft',
                fill: '#EF4444',
                fontSize: 9,
                fontWeight: 600,
              }}
            />
          )}
          {boundaryOverlays?.timeStopTimestamp && (
            <ReferenceLine
              x={boundaryOverlays.timeStopTimestamp}
              stroke="#F59E0B"
              strokeWidth={1.5}
              strokeDasharray="5 4"
              strokeOpacity={0.85}
              label={{
                value: 'Time stop',
                position: 'top',
                fill: '#F59E0B',
                fontSize: 9,
                fontWeight: 600,
              }}
            />
          )}
          {boundaryOverlays?.profitCurveRel && boundaryOverlays.profitCurveRel.length >= 2 && (
            <Line
              data={boundaryOverlays.profitCurveRel}
              dataKey="value"
              xAxisId={0}
              yAxisId={0}
              type="monotone"
              stroke="#10B981"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          )}
          {boundaryOverlays?.deltaCurveRel && boundaryOverlays.deltaCurveRel.length >= 2 && (
            <Line
              data={boundaryOverlays.deltaCurveRel}
              dataKey="value"
              xAxisId={0}
              yAxisId={0}
              type="monotone"
              stroke="#6366F1"
              strokeWidth={1.5}
              strokeDasharray="2 3"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          )}

          {variant === 'plain' && chartData.length > 1 && (
            <ReferenceLine y={chartData[0].relativePrice} stroke="rgba(255,255,255,0.2)" strokeDasharray="2 3" ifOverflow="extendDomain" />
          )}
          {variant === 'plain' && chartData.length > 1 && (
            <ReferenceDot
              x={chartData[chartData.length - 1].timestamp}
              y={chartData[chartData.length - 1].relativePrice}
              r={2.5}
              fill={chartData[chartData.length - 1].price >= chartData[0].price ? '#5cc79a' : '#e08a7e'}
              stroke="none"
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
      {variant === 'plain' && chartData.length > 1 && chartData[0].price > 0 && (() => {
        const first = chartData[0].price;
        const last = chartData[chartData.length - 1].price;
        const chg = ((last - first) / first) * 100;
        const hi = Math.max(...chartData.map((d) => d.price));
        const lo = Math.min(...chartData.map((d) => d.price));
        return (
          <div className="msc-plain-label" aria-label={`${periodMonths} months: ${chg >= 0 ? 'up' : 'down'} ${Math.abs(chg).toFixed(1)}%`}>
            <span>{periodMonths}M</span>
            <b className={chg >= 0 ? 'is-gain' : 'is-loss'}>{chg >= 0 ? '+' : '−'}{Math.abs(chg).toFixed(1)}%</b>
            <span>H {hi.toFixed(2)} · L {lo.toFixed(2)}</span>
          </div>
        );
      })()}


      {/* Fullscreen button */}
      {onOpenFullscreen && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenFullscreen();
          }}
          className="mini-chart-fullscreen-btn"
          title="View fullscreen chart"
          style={{
            position: 'absolute',
            top: '6px',
            left: '6px',
            width: '28px',
            height: '28px',
            background: 'rgba(0, 0, 0, 0.6)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
            color: '#9CA3AF',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20,
            opacity: 0,
            transition: 'all 0.2s ease',
            padding: 0
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
          </svg>
        </button>
      )}

      {/* Compact legend */}
      {showLegend && ((entryPrice && entryDate) || (expiryDate && strikePrice) || (legHistory && legHistory.length > 0)) ? (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          fontSize: '9px',
          fontWeight: '500',
          color: '#9CA3AF',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          padding: '4px 6px',
          borderRadius: '4px',
          lineHeight: '1.2',
          pointerEvents: 'none',
          zIndex: 15,
          minWidth: '60px'
        }}>
          {/* Previous leg entries - subtle */}
          {legHistory && legHistory.length > 0 && (
            <div style={{ marginBottom: '2px', opacity: 0.7 }}>
              <span style={{ color: '#6B7280' }}>●</span> Prior legs: {legHistory.length}
            </div>
          )}
          {entryPrice && entryDate && (
            <div style={{ marginBottom: '2px' }}>
              <span style={{ color: '#F59E0B' }}>●</span> Entry: ${entryPrice.toFixed(2)}
            </div>
          )}
          {expiryDate && strikePrice && (
            <div>
              <span style={{ color: '#DC2626' }}>●</span> Expiry: ${strikePrice.toFixed(2)}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

// Memoize to prevent re-renders when parent updates (e.g. live price polling)
// but chart props haven't changed. realtimePrice is excluded because the chart
// only uses it during initial data load (hasLoaded guard prevents re-processing).
// Memoize to prevent re-renders when parent updates (e.g. live price polling)
// but chart props haven't changed.
// Excluded from comparison:
//  - realtimePrice: only used during initial data load (hasLoaded guard)
//  - onOpenFullscreen: click handler, doesn't affect chart rendering
//  - legHistory: callers use stable constant reference
//
// BAB-68 lesson: any new render-affecting prop MUST be added below or the
// memo silently freezes the child on the first prop reference it accepts.
// Symptom for `boundaries`: decision-map lines stuck at default thresholds
// even after the user saved new thresholds and hard-refreshed the page.
export default React.memo(MiniStockChart, (prev, next) => {
  return (
    prev.symbol === next.symbol &&
    prev.height === next.height &&
    prev.strikePrice === next.strikePrice &&
    prev.entryPrice === next.entryPrice &&
    prev.entryPremium === next.entryPremium &&
    prev.entryDate === next.entryDate &&
    prev.expiryDate === next.expiryDate &&
    prev.periodMonths === next.periodMonths &&
    prev.timespan === next.timespan &&
    prev.currentDelta === next.currentDelta &&
    prev.chartData === next.chartData &&
    prev.showLegend === next.showLegend &&
    // BAB-68: must be checked or the per-position decision-map overlay
    // freezes on the first boundaries reference it ever received.
    prev.boundaries === next.boundaries &&
    prev.variant === next.variant
  );
});